import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  constructor() {
    // One-time cleanup of legacy oversized cache blobs. Older builds stored
    // the full tags/vendorTags lists (multi-MB) before the size guards below
    // existed; the guards skip reading them but never deleted them, so
    // browsers that have them keep paying a heavy localStorage load on every
    // boot (incognito was fast, normal windows were not). Remove 'tags'
    // outright and anything over the current size cap.
    try {
      localStorage.removeItem('tags');
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key) { continue; }
        const item = localStorage.getItem(key);
        if (item && item.length > 500 * 1024) {
          localStorage.removeItem(key);
          console.warn(`Removed oversized legacy localStorage entry '${key}' (${(item.length / 1024).toFixed(0)}KB)`);
        }
      }
    } catch (e) {
      console.error('localStorage legacy cleanup failed:', e);
    }
  }

  public setItem(key: string, value: any): void {
    // Never store tags in localStorage - they are too large
    if (key === 'tags') {
      return;
    }

    try {
      // Check the size of the data before storing
      const dataStr = JSON.stringify(value);
      const sizeInBytes = new Blob([dataStr]).size;
      const sizeInKB = sizeInBytes / 1024;

      // Skip storing very large items (>500KB) in localStorage
      const MAX_ITEM_SIZE_KB = 500;
      if (sizeInKB > MAX_ITEM_SIZE_KB) {
        console.warn(`Data for key '${key}' is too large (${sizeInKB.toFixed(2)}KB). Skipping localStorage storage.`);
        return;
      }

      localStorage.setItem(key, dataStr);
    } catch (e) {
      // Handle QuotaExceededError or other storage errors
      if (e instanceof Error && (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
        console.warn(`localStorage quota exceeded for key: ${key}.`);

        // Clear old/large items to make room
        const largeCacheKeys = ['tags', 'shippingTags', 'vendorTags', 'brands', 'types'];
        let cleared = false;

        for (const cacheKey of largeCacheKeys) {
          if (cacheKey !== key) {
            try {
              const item = localStorage.getItem(cacheKey);
              if (item) {
                const itemSize = new Blob([item]).size / 1024;
                if (itemSize > 100) { // Clear items larger than 100KB
                  localStorage.removeItem(cacheKey);
                  console.log(`Cleared ${cacheKey} (${itemSize.toFixed(2)}KB) from localStorage`);
                  cleared = true;
                }
              }
            } catch (removeError) {
              console.error(`Failed to remove ${cacheKey}:`, removeError);
            }
          }
        }

        // Try one more time after clearing if we cleared something
        if (cleared) {
          try {
            localStorage.setItem(key, JSON.stringify(value));
            console.log(`Successfully stored ${key} after clearing cache`);
          } catch (retryError) {
            // If it still fails, just skip caching
            console.warn(`Unable to store ${key} in localStorage. Data will be fetched from server on next load.`);
          }
        }
      } else {
        console.error('localStorage error:', e);
      }
    }
  }

  public getItem(key: string): any {
    // Never retrieve tags from localStorage
    if (key === 'tags') {
      return null;
    }

    try {
      const item: any = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (e) {
      console.error(`Error parsing localStorage item ${key}:`, e);
      return null;
    }
  }

  public removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  public clear(): void {
    localStorage.clear();
  }

  public getTagFilter(): any {
    return this.getItem('tagFilter');
  }

  public setTagFilter(value: any): void {
    this.setItem('tagFilter', value);
  }
}
